import React, { useEffect, useMemo, useRef, useState } from "react";
import { select } from "d3";
import { normalizeDirectionKey, riskSeverity } from "./koormaUtils";

type RegionalRiskLike = {
  koorma_direction: string;
  risk_score_weighted: number;
  primary_threat?: string | null;
  top_industries?: string[];
};

type KoormaTortoiseProps = {
  risks: RegionalRiskLike[];
  selectedDirection: string | null;
  onSelectDirection: (direction: string) => void;
};

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function getRiskColor(score: number) {
  if (score >= 50) return "#ef4444";
  if (score >= 30) return "#f59e0b";
  if (score >= 15) return "#10b981";
  return "#3b82f6";
}

function getRiskGradient(score: number): [string, string] {
  if (score >= 50) return ["#ff6b6b", "#ee5a5a"];
  if (score >= 30) return ["#feca57", "#ff9f43"];
  if (score >= 15) return ["#1dd1a1", "#10ac84"];
  return ["#54a0ff", "#2e86de"];
}

const DIRECTIONS: Array<{ name: string; angle: number; sanskrit: string }> = [
  { name: "North", angle: -90, sanskrit: "उत्तर" },
  { name: "North-East", angle: -45, sanskrit: "ईशान्य" },
  { name: "East", angle: 0, sanskrit: "पूर्व" },
  { name: "South-East", angle: 45, sanskrit: "आग्नेय" },
  { name: "South", angle: 90, sanskrit: "दक्षिण" },
  { name: "South-West", angle: 135, sanskrit: "नैऋत्य" },
  { name: "West", angle: 180, sanskrit: "पश्चिम" },
  { name: "North-West", angle: -135, sanskrit: "वायव्य" },
];

export default function KoormaTortoise({ risks, selectedDirection, onSelectDirection }: KoormaTortoiseProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState(300);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver(() => {
      const width = el.offsetWidth;
      const next = clamp(width - 8, 200, 320);
      setSize(next);
    });
    ro.observe(el);
    const width = el.offsetWidth;
    setSize(clamp(width - 8, 200, 320));
    return () => ro.disconnect();
  }, []);

  const riskByDirection = useMemo(() => {
    const map = new Map<string, RegionalRiskLike>();
    for (const r of risks || []) {
      const key = normalizeDirectionKey(r?.koorma_direction || "");
      if (!key) continue;
      if (!map.has(key)) map.set(key, r);
    }
    return map;
  }, [risks]);

  const selectedKey = useMemo(() => normalizeDirectionKey(selectedDirection || ""), [selectedDirection]);

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = select(svgRef.current);
    svg.selectAll("*").remove();

    const cx = size / 2;
    const cy = size / 2;
    const shellRx = size * 0.38;
    const shellRy = size * 0.32;

    const defs = svg.append("defs");

    const shellGrad = defs.append("radialGradient").attr("id", "koorma-shell-grad");
    shellGrad.append("stop").attr("offset", "0%").attr("stop-color", "#2d3748");
    shellGrad.append("stop").attr("offset", "70%").attr("stop-color", "#1a202c");
    shellGrad.append("stop").attr("offset", "100%").attr("stop-color", "#0d1117");

    const goldGrad = defs
      .append("linearGradient")
      .attr("id", "koorma-gold-grad")
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "100%")
      .attr("y2", "100%");
    goldGrad.append("stop").attr("offset", "0%").attr("stop-color", "#d4af37");
    goldGrad.append("stop").attr("offset", "50%").attr("stop-color", "#f4d03f");
    goldGrad.append("stop").attr("offset", "100%").attr("stop-color", "#d4af37");

    const allDirectionKeys = ["Central", ...DIRECTIONS.map((d) => d.name)];
    for (const dir of allDirectionKeys) {
      const normalized = normalizeDirectionKey(dir);
      const r = riskByDirection.get(normalized);
      const score = r?.risk_score_weighted ?? 0;
      const [c1, c2] = getRiskGradient(score);
      const grad = defs.append("radialGradient").attr("id", `koorma-risk-${normalized}`);
      grad.append("stop").attr("offset", "0%").attr("stop-color", c1);
      grad.append("stop").attr("offset", "100%").attr("stop-color", c2);
    }

    const glow = defs
      .append("filter")
      .attr("id", "koorma-node-glow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%");
    glow.append("feGaussianBlur").attr("stdDeviation", "4").attr("result", "blur");
    glow.append("feComposite").attr("in", "SourceGraphic").attr("in2", "blur").attr("operator", "over");

    svg
      .append("style")
      .text("@keyframes koorma-pulse{0%,100%{opacity:0.5;transform-origin:center;}50%{opacity:0.2;}}");

    const g = svg.append("g");

    g.append("ellipse")
      .attr("cx", cx)
      .attr("cy", cy)
      .attr("rx", shellRx)
      .attr("ry", shellRy)
      .attr("fill", "url(#koorma-shell-grad)")
      .attr("stroke", "url(#koorma-gold-grad)")
      .attr("stroke-width", 2);

    const shellPatternAngles = [0, 60, 120, 180, 240, 300];
    for (const angle of shellPatternAngles) {
      const rad = (angle * Math.PI) / 180;
      const x1 = cx + Math.cos(rad) * shellRx * 0.3;
      const y1 = cy + Math.sin(rad) * shellRy * 0.3;
      const x2 = cx + Math.cos(rad) * shellRx * 0.85;
      const y2 = cy + Math.sin(rad) * shellRy * 0.85;
      g.append("line")
        .attr("x1", x1)
        .attr("y1", y1)
        .attr("x2", x2)
        .attr("y2", y2)
        .attr("stroke", "rgba(212,175,55,0.15)")
        .attr("stroke-width", 1);
    }

    g.append("ellipse")
      .attr("cx", cx)
      .attr("cy", cy)
      .attr("rx", shellRx * 0.5)
      .attr("ry", shellRy * 0.5)
      .attr("fill", "none")
      .attr("stroke", "rgba(212,175,55,0.2)")
      .attr("stroke-width", 1);

    g.append("ellipse")
      .attr("cx", cx)
      .attr("cy", cy - shellRy - size * 0.06)
      .attr("rx", size * 0.06)
      .attr("ry", size * 0.045)
      .attr("fill", "#1a202c")
      .attr("stroke", "#d4af37")
      .attr("stroke-width", 1.5);
    g.append("circle")
      .attr("cx", cx - 6)
      .attr("cy", cy - shellRy - size * 0.065)
      .attr("r", 2)
      .attr("fill", "#d4af37");
    g.append("circle")
      .attr("cx", cx + 6)
      .attr("cy", cy - shellRy - size * 0.065)
      .attr("r", 2)
      .attr("fill", "#d4af37");

    g.append("path")
      .attr(
        "d",
        `M${cx} ${cy + shellRy} L${cx - 8} ${cy + shellRy + 18} L${cx} ${cy + shellRy + 12} L${cx + 8} ${
          cy + shellRy + 18
        } Z`,
      )
      .attr("fill", "#1a202c")
      .attr("stroke", "#d4af37")
      .attr("stroke-width", 1);

    const legPositions = [
      { x: cx - shellRx - 8, y: cy - shellRy * 0.4, rot: -20 },
      { x: cx + shellRx + 8, y: cy - shellRy * 0.4, rot: 20 },
      { x: cx - shellRx - 8, y: cy + shellRy * 0.4, rot: 20 },
      { x: cx + shellRx + 8, y: cy + shellRy * 0.4, rot: -20 },
    ];
    for (const leg of legPositions) {
      g.append("ellipse")
        .attr("cx", leg.x)
        .attr("cy", leg.y)
        .attr("rx", size * 0.055)
        .attr("ry", size * 0.035)
        .attr("fill", "#1a202c")
        .attr("stroke", "#d4af37")
        .attr("stroke-width", 1)
        .attr("transform", `rotate(${leg.rot} ${leg.x} ${leg.y})`);
    }

    const maxRisk = Math.max(1, ...[...riskByDirection.values()].map((r) => Number(r?.risk_score_weighted || 0)));

    const centerRisk = riskByDirection.get(normalizeDirectionKey("Central"))?.risk_score_weighted ?? 0;
    const isSelectedCenter = selectedKey === normalizeDirectionKey("Central");
    const centerSeverity = riskSeverity(centerRisk);

    g.append("circle")
      .attr("cx", cx)
      .attr("cy", cy)
      .attr("r", isSelectedCenter ? 22 : 18)
      .attr("fill", `url(#koorma-risk-${normalizeDirectionKey("Central")})`)
      .attr("stroke", isSelectedCenter ? "#fff" : "#d4af37")
      .attr("stroke-width", isSelectedCenter ? 3 : 1.5)
      .attr("filter", centerSeverity === "high" ? "url(#koorma-node-glow)" : "")
      .style("cursor", "pointer")
      .on("click", () => onSelectDirection("Central"));

    if (centerSeverity === "high") {
      g.append("circle")
        .attr("cx", cx)
        .attr("cy", cy)
        .attr("r", (isSelectedCenter ? 22 : 18) + 10)
        .attr("fill", "none")
        .attr("stroke", getRiskColor(centerRisk))
        .attr("stroke-width", 2)
        .attr("opacity", 0.5)
        .style("animation", "koorma-pulse 2s infinite");
    }

    g.append("text")
      .attr("x", cx)
      .attr("y", cy + 4)
      .attr("text-anchor", "middle")
      .attr("fill", "#fff")
      .attr("font-size", "11px")
      .attr("font-weight", "bold")
      .style("pointer-events", "none")
      .text(Math.round(centerRisk));

    for (const dir of DIRECTIONS) {
      const key = normalizeDirectionKey(dir.name);
      const r = riskByDirection.get(key);
      const score = r?.risk_score_weighted ?? 0;
      const severity = riskSeverity(score);

      const isSelected = selectedKey === key;
      const rad = (dir.angle * Math.PI) / 180;
      const nodeDistance =
        dir.angle % 90 === 0 ? (dir.angle === -90 || dir.angle === 90 ? shellRy * 0.75 : shellRx * 0.78) : Math.min(shellRx, shellRy) * 0.72;
      const x = cx + Math.cos(rad) * nodeDistance;
      const y = cy + Math.sin(rad) * nodeDistance;

      const baseSize = 14 + (score / maxRisk) * 10;
      const nodeSize = isSelected ? baseSize + 4 : baseSize;

      if (severity === "high") {
        g.append("circle")
          .attr("cx", x)
          .attr("cy", y)
          .attr("r", nodeSize + 8)
          .attr("fill", "none")
          .attr("stroke", getRiskColor(score))
          .attr("stroke-width", 2)
          .attr("opacity", 0.5)
          .style("animation", "koorma-pulse 2s infinite");
      }

      if (isSelected) {
        g.append("circle").attr("cx", x).attr("cy", y).attr("r", nodeSize + 4).attr("fill", "none").attr("stroke", "#fff").attr("stroke-width", 2);
      }

      g.append("circle")
        .attr("cx", x)
        .attr("cy", y)
        .attr("r", nodeSize)
        .attr("fill", `url(#koorma-risk-${key})`)
        .attr("stroke", isSelected ? "#fff" : "rgba(255,255,255,0.3)")
        .attr("stroke-width", isSelected ? 2 : 1)
        .attr("filter", severity === "high" ? "url(#koorma-node-glow)" : "")
        .style("cursor", "pointer")
        .on("click", () => onSelectDirection(dir.name));

      g.append("text")
        .attr("x", x)
        .attr("y", y + 4)
        .attr("text-anchor", "middle")
        .attr("fill", "#fff")
        .attr("font-size", "10px")
        .attr("font-weight", "bold")
        .style("pointer-events", "none")
        .text(Math.round(score));

      const labelDist = nodeDistance + 28;
      const lx = cx + Math.cos(rad) * labelDist;
      const ly = cy + Math.sin(rad) * labelDist;
      g.append("text")
        .attr("x", lx)
        .attr("y", ly + 4)
        .attr("text-anchor", "middle")
        .attr("fill", isSelected ? "#d4af37" : "rgba(212,175,55,0.7)")
        .attr("font-size", "10px")
        .attr("font-weight", isSelected ? "600" : "400")
        .style("pointer-events", "none")
        .text(dir.sanskrit);
    }
  }, [onSelectDirection, riskByDirection, selectedKey, size]);

  return (
    <div ref={containerRef} className="w-full overflow-hidden">
      <svg
        ref={svgRef}
        width={size}
        height={size}
        className="mx-auto block"
        role="img"
        aria-label="Koorma Chakra risk tortoise"
      />
    </div>
  );
}
